import { useState } from "react";

import { useQueries, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { AutoTagCandidate } from "@/apis/table/types";
import { useSelectedTable } from "@/components/mapping/hooks";
import {
  destructTagKey,
  findCellByAddress,
  tagKey,
} from "@/components/mapping/utils";
import { useMappingInfos } from "@/components/modals/mapping-info/hooks";
import useDragTagging from "@/hooks/mutations/use-drag-tag";
import { useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { useMappingStore } from "@/stores/mapping";

/** 후보 한 건을 식별하는 안정적 key (React key + 적용 중 표시용). */
export const candidateKey = (c: AutoTagCandidate) =>
  `${c.tagType}:${c.axisKey ?? ""}:${c.rank}:${c.tagKey}`;

/** tagKey 한 건의 라벨 조회 상태. `getTagDetail`(tagKey 직접 조회) 결과를 담는다. */
export type TagLabelInfo = {
  /** getTagDetail 가 준 한국어 라벨. 로딩 중·미등록(404)이면 undefined. */
  koLabel?: string;
  isLoading: boolean;
  /** 조회 성공 = 분류체계에 등록된 태그 = 적용 가능. 404(미생성 제안)면 false. */
  isResolved: boolean;
};

export type TagLabelMap = Map<string, TagLabelInfo>;

/** 후보 표시 라벨. getTagDetail 한국어 라벨 우선, 없으면 proposal 라벨 > tagKey 이름부. */
export const labelOf = (
  info: TagLabelInfo | undefined,
  candidate: AutoTagCandidate,
): string => {
  if (info?.koLabel) return info.koLabel;
  return (
    candidate.proposalKoLabel ??
    candidate.proposalEnLabel ??
    destructTagKey(candidate.tagKey).name ??
    candidate.tagKey
  );
};

/** 축 제목 라벨. getTagDetail 한국어 라벨 우선, 없으면 axisKey 이름부. */
export const axisLabelOf = (
  info: TagLabelInfo | undefined,
  axisKey: string,
): string => info?.koLabel ?? destructTagKey(axisKey).name ?? axisKey;

/**
 * 선택된 셀의 auto-tagging 후보를 조회한다. 셀 목록의 `isAutoTagged` 마커가 있는
 * 셀에 대해서만 endpoint 를 호출(lazy gating) — 오토태깅 안 한 셀은 요청 0회.
 */
export const useCellAutoTagInfo = () => {
  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId },
  } = useSelectedTable();
  const address = useMappingStore((s) => s.fact.selected?.address);

  const { data: cellsList } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells(),
    enabled: !!workbookId && !!tableId,
  });
  // 머지 셀은 목록에 "D10:D12" 로 들어오는데 선택 주소는 앵커("D10")일 수 있다.
  // 주소를 문자열로 비교하면 머지 셀에서 마커를 못 찾아 요청이 아예 안 나간다.
  const cell = findCellByAddress(cellsList?.cells, address);
  const isAutoTagged = !!cell?.isAutoTagged;

  const { data, isLoading } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      // 백엔드 조회는 주소 완전일치라, 앵커가 아니라 목록의 정본 주소로 보낸다.
      ._ctx.cells(cell?.address)
      ._ctx.autoTagInfo(),
    enabled: !!workbookId && !!tableId && !!cell?.address && isAutoTagged,
    staleTime: 1000 * 60 * 5,
  });

  return {
    candidates: data?.candidates ?? [],
    isAutoTagged,
    isLoading,
  };
};

/**
 * 여러 tagKey 의 한국어 라벨을 `getTagDetail`(GET /taxonomy/tags/{tagKey})로 한 번에
 * 조회한다. tagKey 로 직접 조회하므로 셀 검색 트리에 없는 회사확장·적용 해제 태그도
 * 라벨이 나온다. 미생성 제안 키는 404 → `isResolved=false` 로 "적용 불가" 신호.
 * `useQueries` 한 번 호출이라 후보 수가 변해도 hooks 규칙에 안전하다.
 */
export const useTagLabels = (tagKeys: string[]): TagLabelMap => {
  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId },
  } = useSelectedTable();
  const on = !!workbookId && !!tableId;

  const results = useQueries({
    queries: tagKeys.map((key) => ({
      ...queries
        .workbook(workbookId!)
        ._ctx.taxonomy()
        ._ctx.tags(key, tableId ?? undefined),
      enabled: on && !!key,
      staleTime: 1000 * 60 * 5,
      // 미등록 키는 404 — 재시도하지 않고 즉시 미해결로 처리한다.
      retry: false,
    })),
  });

  const map: TagLabelMap = new Map();
  tagKeys.forEach((key, i) => {
    const r = results[i];
    map.set(key, {
      koLabel: r.data?.detail?.koLabel,
      isLoading: r.isLoading,
      isResolved: r.isSuccess,
    });
  });
  return map;
};

/**
 * 후보 클릭 → 드래그 태깅 API 로 재태깅한다. 단일 셀/범위 선택 모두 같은 경로로 흐르며,
 * 후보가 화면에 보여준 축(axisKey)을 함께 보내 같은 축 기존 멤버가 교체되도록 한다.
 * 후보 타입에 맞는 차원을 명시 주입한다(현재 탭과 달라도 정확히 LINE_ITEM/AXIS 로).
 * `appliedKeys` 로 현재 적용된 후보를 표시하고, `applyingKey` 로 진행 중 후보를 표시.
 */
export const useApplyCandidate = () => {
  const selectedFact = useMappingStore((s) => s.fact.selected);
  const { tableId, workbookId } = useMappingInfos({ toToast: false });

  // 적용됨 표시의 기준이 되는 "현재 셀 태그"는 서버 백업 쿼리에서 읽는다. 드래그 mutation
  // 이 무효화하는 대상과 같은 쿼리라, 재태깅 후 refetch 되면 같은 축의 옛 멤버가 자동으로
  // 적용됨에서 풀린다.
  const { data: cellTags } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.tables(tableId!)
      ._ctx.cells(selectedFact?.address)
      ._ctx.tags(),
    enabled: !!workbookId && !!tableId && !!selectedFact?.address,
  });
  const currentTags = cellTags?.tags ?? [];

  const { mutate: dragLineItem } = useDragTagging(undefined, "LINE_ITEM");
  const { mutate: dragMember } = useDragTagging(undefined, "AXIS");

  const [applyingKey, setApplyingKey] = useState<string | null>(null);

  const appliedKeys = new Set(currentTags.map((t) => tagKey(t.prefix, t.name)));

  const apply = (candidate: AutoTagCandidate) => {
    if (!workbookId || !tableId || !selectedFact) return;
    setApplyingKey(candidateKey(candidate));

    const drag = candidate.tagType === "LINE_ITEM" ? dragLineItem : dragMember;
    drag(
      { tagKey: candidate.tagKey, axisKey: candidate.axisKey },
      {
        onSuccess: () => toast.success("태그를 적용했습니다."),
        onError: () => toast.error("태그 적용에 실패했습니다."),
        onSettled: () => setApplyingKey(null),
      },
    );
  };

  return { apply, applyingKey, appliedKeys };
};
